import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import {
  allLocations,
  addFavourite,
  removeFavourite,
  getFilteredProducts,
} from '../../../services/product';
import { viewUser } from '../../../services/user';
import { isAuthenticated } from '../../../services/auth';
import moment from 'moment';
import { prices } from '../../../components/ui/inputs/price-range';
import ProductCard from '../../../components/product/product-card';
import { MUIPagination } from '../../../components/ui/pagination';
import {
  Empty,
  Tooltip,
  message,
} from 'antd';
import { Drawer, Typography, TextField, IconButton, InputAdornment, Radio, RadioGroup, FormControlLabel, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import Footer from '../../../components/layout/footer';

const API = (process.env.REACT_APP_API ).replace(/\/api$/, '');

const CategoryView = ({ match }) => {
  const countPerPage = 10;
  const [products, setProducts] = useState([]);
  const [current, setCurrent] = useState(1);
  const [pagination, setPagination] = useState([]);
  const [favourites, setFavourites] = useState([]);
  const [favourite, setFavourite] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationVisible, setLocationVisible] = useState(false);
  const [filter, setFilter] = useState({
    condition: '',
    price: [],
    category: match.params.categoryId,
    location: '',
  });
  const [min, setMin] = useState('');
  const [max, setMax] = useState('');
  const [locationName, setLocationName] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [sortBy, setSortBy] = useState('-1');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');

  const { user, token } = isAuthenticated();
  const { condition, price, location } = filter;

  // Responsividade
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Carregar dados
  useEffect(() => {
    const loadUser = async () => {
      if (user && token) {
        const res = await viewUser(user._id);
        setFavourites(res.data.favourites);
      }
    };
    const loadLocations = async () => {
      const res = await allLocations();
      setLocations(res.data);
    };
    loadUser();
    loadLocations();
  }, []);

  useEffect(() => {
    setFilter(prev => ({ ...prev, category: match.params.categoryId }));
  }, [match.params.categoryId]);

  useEffect(() => {
    const loadFilteredResults = async (page = 1) => {
      const res = await getFilteredProducts(filter, sortBy);
      setCurrent(page);
      const to = page * countPerPage;
      const from = to - countPerPage;
      setPagination(res.data.slice(from, to));
      setProducts(res.data);
      if (res.data.length > 0) {
        setCategoryName(res.data[0].category.name);
      }
    };
    loadFilteredResults(current);
    // eslint-disable-next-line
  }, [price, condition, sortBy, location, favourite, current, match.params.categoryId]);

  // Ativos
  const checkActive = products.some(product => product.status === 'active');

  // Handlers
  const handleSorted = (e) => setSortBy(e.target.value);
  const handleCondition = (e) => setFilter({ ...filter, condition: e.target.value });
  const handlePriceRange = (e) => {
    setFilter({ ...filter, price: e.target.value });
    setMin('');
    setMax('');
  };
  const handleAddFavourite = async (productId) => {
    await addFavourite(productId, { user, token });
    // Atualiza a lista de favoritos imediatamente
    setFavourites((prev) => [...prev, productId]);
    message.success('Adicionado aos favoritos');
  };
  const handleRemoveFavourite = async (productId) => {
    await removeFavourite(productId, { user, token });
    // Remove da lista de favoritos imediatamente
    setFavourites((prev) => prev.filter((id) => id !== productId));
    message.error('Removido dos favoritos');
  };
  Number.prototype.format = function (n = 0, x = 3) {
    var re = '\\d(?=(\\d{' + x + '})+' + (n > 0 ? '\\.' : '$') + ')';
    return this.toFixed(Math.max(0, ~~n)).replace(new RegExp(re, 'g'), '$&,');
  };
  const handleLocation = () => {
    setLocationVisible(true);
  };
  const handleLocationFilter = (id, name) => {
    setFilter({ ...filter, location: id });
    setLocationVisible(false);
    setLocationName(name || '');
    setLocationSearch('');
  };
  const handleLocationCLose = () => {
    setLocationVisible(false);
    setLocationSearch('');
  };

  const handleMin = (e) => setMin(e.target.value);
  const handleMax = (e) => setMax(e.target.value);
  const handleSubmitPrice = () => {
    if (!min || !max) {
      message.error('Preencha os dois campos de preço');
      return;
    }
    setFilter({ ...filter, price: [parseInt(min), parseInt(max)] });
  };

  // Drawer de filtro mobile
  const FilterDrawer = () => (
    <Drawer
      anchor="left"
      open={drawerVisible}
      onClose={() => setDrawerVisible(false)}
      hideBackdrop={true}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMobile ? '90vw' : 350,
          backgroundColor: '#ffffff',
          borderRadius: '0 20px 20px 0',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          padding: 0,
        },
      }}
    >
      <div style={{ padding: 24, position: 'relative' }}>
        <IconButton
          onClick={() => setDrawerVisible(false)}
          sx={{ position: 'absolute', top: 16, right: 16 }}
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#333' }}>
          Filtrar
        </Typography>
        {FilterContent()}
      </div>
    </Drawer>
  );

  // Drawer de localização
  const LocationDrawer = () => {
    const filteredLocations = useMemo(() => locations.filter(l => l.name.toLowerCase().includes(locationSearch.toLowerCase())), [locations, locationSearch]);
    return (
      <Drawer
        anchor="left"
        open={locationVisible}
        onClose={handleLocationCLose}
        hideBackdrop={true}
        ModalProps={{ style: { zIndex: 1400 } }}
        sx={{
          '& .MuiDrawer-paper': {
            width: isMobile ? '90vw' : 350,
            backgroundColor: '#ffffff',
            borderRadius: '0 20px 20px 0',
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            padding: 0,
          },
        }}
      >
        <div style={{ padding: 24, position: 'relative' }}>
          <IconButton
            onClick={handleLocationCLose}
            sx={{ position: 'absolute', top: 16, right: 16 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#333' }}>
            Localizações
          </Typography>
          <TextField
            label="Buscar Localização"
            value={locationSearch}
            onChange={(e) => setLocationSearch(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            autoFocus
          />
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            <Button
              variant="text"
              fullWidth
              onClick={() => handleLocationFilter('', '')}
            >
              Todas as Localizações
            </Button>
            {filteredLocations.map((l, i) => (
              <Button
                key={i}
                variant="text"
                fullWidth
                onClick={() => handleLocationFilter(l._id, l.name)}
              >
                {l.name}
              </Button>
            ))}
          </div>
        </div>
      </Drawer>
    );
  };
  const FilterContent = () => (
    <div className="modern-filter-container">
      <Button
        variant="text"
        onClick={handleLocation}
      >
        <i className='fas fa-map-marked-alt me-2'></i>
        {location === '' ? 'Selecionar Localização' : `${locationName} - Estado`}
      </Button>
      <strong>
        <p className='text-dark1 mb-2 mt-3 letter-spacing-02'>Condição</p>
      </strong>
      <RadioGroup onChange={handleCondition} value={condition} sx={{ mb: 2 }}>
        <FormControlLabel value="" control={<Radio />} label="Qualquer" />
        <FormControlLabel value="New" control={<Radio />} label="Novo" />
        <FormControlLabel value="Used" control={<Radio />} label="Usado" />
      </RadioGroup>
      <hr className="margin-hr" />
      <strong>
        <p className='text-dark1 mb-2'>Faixa de preço</p>
      </strong>
      <div className='mb-2'>
        <RadioGroup onChange={handlePriceRange} value={price}>
          {prices.map((p, i) => (
            <FormControlLabel
              key={i}
              value={p.array}
              control={<Radio />}
              label={p.name.replace('to', 'até').replace('Above', 'Acima de').replace('Any', 'Qualquer').replace('Price', 'Preço').replace('R$', 'R$ ')}
            />
          ))}
        </RadioGroup>
      </div>
      <span className="modern-price-label">Preço personalizado:</span>
      <div className='d-flex align-items-center mb-3'>
        <TextField
          label="Mín"
          type="number"
          value={min}
          onChange={handleMin}
          size="small"
          sx={{ mr: 1, width: 120 }}
          InputProps={{
            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
          }}
        />
        <Typography sx={{ mx: 1 }}>até</Typography>
        <TextField
          label="Máx"
          type="number"
          value={max}
          onChange={handleMax}
          size="small"
          sx={{ mr: 1, width: 120 }}
          InputProps={{
            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
          }}
        />
        <Button
          onClick={handleSubmitPrice}
          variant="contained"
        >
          OK
        </Button>
      </div>
    </div>
  );

  // Renderizar card de produto usando o componente ProductCard
  const renderProductCard = (product) => (
    <ProductCard
      key={product._id}
      product={product}
      showActions={false}
      showAuthor={true}
    />
  );

  return (
    <>
      <div className='container-fluid px-1 px-md-4 mb-5 category-container'>
      <Breadcrumb className="mb-3">
        <Breadcrumb.Item>
          <Link to='/' className='text-decoration-none'>
            Início
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item active>{categoryName}</Breadcrumb.Item>
      </Breadcrumb>

      <div className="row align-items-stretch">
        {/* Filtro lateral desktop */}
        {!isMobile && (
          <div className='col-md-3 mb-3'>
            <div className='card rounded-3 card-shadow category-sidebar h-100'>
              <div className='card-body p-3'>
                {FilterContent()}
              </div>
            </div>
          </div>
        )}

        {/* Conteúdo principal */}
        <div className={isMobile ? 'col-12' : 'col-md-9'}>
          {/* Botão filtro mobile */}
          {isMobile && (
            <>
              <Button
                variant="contained"
                sx={{ width: '100%', mb: 3 }}
                onClick={() => setDrawerVisible(true)}
              >
                <i className="fas fa-filter me-2"></i>Filtrar
              </Button>
              {FilterDrawer()}
            </>
          )}

          <div className='card rounded-3 card-shadow mb-3 h-100'>
              <div className='header-category'>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2 className={`category-header-title ${isMobile ? 'category-header-title-mobile' : ''} mb-0`}>
                    {products.length > 0
                      ? `${products[0].category.name} ${location ? `em ${locationName}` : '- Brasil'}`
                      : 'Nenhum produto encontrado'}
                  </h2>
                </div>
                <div style={{ flex: isMobile ? '1 1 100%' : '0 0 auto', minWidth: isMobile ? '100%' : 'auto' }}>
                  <p className='mb-2' style={{ fontSize: '13px', fontWeight: '500', color: '#6c757d' }}>
                    Ordenar por:
                  </p>
                  <select
                    className='form-select form-select-sm shadow-none'
                    style={{ width: '100%' }}
                    onChange={handleSorted}
                  >
                    <option value='-1'>Mais recentes</option>
                    <option value='1'>Mais antigos</option>
                  </select>
                </div>
            </div>
            <div className='container-products'>
              {!checkActive && <Empty description="Nenhum produto encontrado" />}
              {checkActive && (
                <>
                  {pagination.filter(p => p.status === 'active').length === 0 && (
                    <div className="text-center text-muted py-5">Nenhum produto encontrado.</div>
                  )}
                  <div className='products-grid'>
                    {pagination.filter(p => p.status === 'active').map(renderProductCard)}
                  </div>
                  <div className="pagination-container mt-4" style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <MUIPagination
                      count={Math.ceil(products.length / countPerPage)}
                      page={current}
                      onChange={page => setCurrent(page)}
                      color="primary"
                      size="medium"
                      showFirstButton={Math.ceil(products.length / countPerPage) > 5}
                      showLastButton={Math.ceil(products.length / countPerPage) > 5}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
      <LocationDrawer />
      <FilterDrawer />
    </>
  );
};

export default CategoryView;
